import { useDisclosure } from "@shared/hooks/useDisclosure";
import { useColorMode } from "@shared/hooks/useColorMode";
import Spacer from "@ui-kit/layout/Spacer/Spacer.web";
import NavBarLocaleMenu from "@shared/components/application/navigation/NavBarLocaleMenu/NavBarLocaleMenu.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import { domElementIds, LOGO_DIMENSIONS } from "@shared/constants/common";
import { APP_ENV } from "@shared/env";
import useNavigationRoutes from "@shared/hooks/navigation/useNavigationRoutes";
import useWindowSize from "@shared/hooks/useWindowSize";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { navigationLocales } from "@shared/modules/navigation/constants/navigation-translations";
import { useNavBarActions } from "@shared/modules/navigation/hooks/useNavBarActions";
import breakpoints from "@shared/theme/props/breakpoints";
import { isPlatform } from "@shared/utils/get-platform";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import url from "@shared/utils/urls/url";
import NavBarMenu from "@ui-application/navigation/NavBarMenu/NavBarMenu.web";
import UserMenu from "@ui-application/navigation/UserMenu/UserMenu.web";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import PageMaxWidth from "@ui-kit/layout/PageMaxWidth/PageMaxWidth.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import Cookies from "js-cookie";
import { useContextualRouting } from "next-use-contextual-routing";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import NavBarBanners from "../../data-display/NavBarBanners/NavBarBanners.web";
import NavBarCartDisplay from "../../data-display/NavBarCartDisplay/NavBarCartDisplay.web";
import NavBarMobileModal from "../NavBarMobileModal/NavBarMobileModal.web";
import { NavBarProps } from "./NavBar.props";

const { sm } = breakpoints;
const { navbarId } = domElementIds;

type ContainerProps = React.ComponentProps<typeof Box> & {
  isDetailPage: boolean;
};

const Container: React.FC<ContainerProps> = ({
  isDetailPage,
  children,
  ...props
}) => (
  <Box
    width="100%"
    flexDirection="column"
    height={{
      base: "var(--navbar-mobile-height)",
      sm: "var(--navbar-desktop-height)",
    }}
    alignItems="center"
    justifyContent="center"
    bg="bg"
    zIndex="var(--z-index-sticky)"
    gap="xs"
    top="none"
    display="flex"
    borderBottom="1px solid"
    borderColor="gray.400"
    position={isDetailPage ? undefined : "sticky"}
    boxShadow={isDetailPage ? "none" : undefined}
    {...props}
  >
    {children}
  </Box>
);

type ProfileImageProps = React.ComponentProps<typeof CDNImage>;

const ProfileImage: React.FC<ProfileImageProps> = (props) => (
  <Box
    as="span"
    display="inline-block"
    css={{
      "& img": {
        userSelect: "none",
        borderRadius: "full",
      },
    }}
  >
    <CDNImage {...props} />
  </Box>
);

const NavBar = ({
  isDetailPage = false,
  isReactNativeApp = false,
}: NavBarProps) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { makeContextualHref } = useContextualRouting();
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();
  const { loggedInLinks, goToLocale, handleLogout } = useNavBarActions();
  const { colorMode } = useColorMode();
  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => windowWidth && windowWidth < sm - 1,
    [windowWidth]
  );
  const { loggedOutLinks } = useNavigationRoutes();
  const {
    isOpen: isMobileMenuOpen,
    onOpen: mobileMenuOnOpen,
    onClose: mobileMenuOnClose,
  } = useDisclosure();

  const logoSrc =
    colorMode === "dark"
      ? "images/logos/showpass-logo-white.svg"
      : "images/logos/showpass-logo-red.svg";

  const logInOnClick = () => {
    mobileMenuOnClose();

    router.push(
      makeContextualHref({
        accountModal: "login",
      }),
      "/accounts/login/",
      {
        shallow: true,
      }
    );
  };

  const signUpOnClick = () => {
    mobileMenuOnClose();

    router.push(
      makeContextualHref({
        accountModal: "createAccount",
      }),
      "/accounts/signup/",
      {
        shallow: true,
      }
    );
  };

  // Close the mobile modal on larger resolution if it is open.
  useEffect(() => {
    if (isMobileMenuOpen && !isSmallerThanSm) {
      mobileMenuOnClose();
    }
  }, [isSmallerThanSm, isMobileMenuOpen, mobileMenuOnClose]);

  const venueCookieExists =
    Boolean(Cookies.get("showpass-venue-id")) &&
    Cookies.get("showpass-venue-id") !== "None";

  return (
    <>
      <Container
        id={navbarId}
        isDetailPage={Boolean(isDetailPage)}
        style={isReactNativeApp ? { display: "none" } : {}}
      >
        <Flex
          direction="column"
          gap={isSmallerThanSm ? "13px" : "18px"}
          width="100%"
        >
          <PageMaxWidth>
            <Flex alignItems="center">
              <NavBarMobileModal
                loggedInLinks={loggedInLinks}
                loggedOutLinks={loggedOutLinks}
                onClose={mobileMenuOnClose}
                isOpen={isMobileMenuOpen}
                locales={navigationLocales}
                goToLocale={goToLocale}
                handleLogout={handleLogout}
                logInOnClick={logInOnClick}
                signUpOnClick={signUpOnClick}
              />
              <NextLink href="/" passHref>
                <Box
                  as="span"
                  data-testid="navbar-showpass-logo"
                  aria-label="showpass logo"
                  display="inline-block"
                  cursor="pointer"
                >
                  <Box mt={LOGO_DIMENSIONS.MARGIN_TOP}>
                    <CDNImage
                      useServerlessResize={false}
                      src={logoSrc}
                      alt="showpass"
                      width={
                        isSmallerThanSm
                          ? LOGO_DIMENSIONS.MOBILE_WIDTH
                          : LOGO_DIMENSIONS.DESKTOP_WIDTH
                      }
                      height={LOGO_DIMENSIONS.HEIGHT}
                      style={{ objectFit: "contain" }}
                      placeholder="empty"
                    />
                  </Box>
                </Box>
              </NextLink>
              <Spacer />

              {/* strictly checking for false to avoid flashing */}
              {isLoggedIn || isLoggedIn === false ? (
                <>
                  <Box display="block">
                    <NavBarCartDisplay isDetailPage={Boolean(isDetailPage)} />
                  </Box>
                  <Box
                    display={{ base: "none", sm: "flex" }}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box w="element.lg" />
                    <NavBarLocaleMenu />
                    <Box w="element.lg" />
                    {/** this button is for development/testing convenience; button links straight to the new manage dashboard */}
                    {(venueCookieExists || user?.impersonator_id) &&
                      (APP_ENV === "local" || APP_ENV === "preview") &&
                      !isDetailPage && (
                        <>
                          <NextLink
                            aria-label="manage"
                            href="/manage/"
                            passHref
                          >
                            <Button.Nav data-testid="navbar-manage">
                              {t("Manage")}
                            </Button.Nav>
                          </NextLink>
                          <Box w="element.lg" />
                        </>
                      )}
                    {isLoggedIn && user ? (
                      <>
                        {(venueCookieExists || user.impersonator_id) && (
                          <>
                            <a
                              aria-label="dashboard"
                              role="button"
                              href={url("dashboard")}
                            >
                              <Button.Nav data-testid="navbar-dashboard">
                                {t("Dashboard")}
                              </Button.Nav>
                            </a>
                            <Box w="element.lg" />
                          </>
                        )}
                        <UserMenu
                          links={loggedInLinks}
                          handleLogout={handleLogout}
                          user={user}
                        />
                      </>
                    ) : (
                      <>
                        {!isDetailPage && (
                          <>
                            <NavBarMenu links={loggedOutLinks} />
                            <Box w="element.lg" />
                          </>
                        )}
                        {!isPlatform("desktop") && (
                          <>
                            <Button.Nav
                              data-testid="navbar-create-account"
                              onClick={signUpOnClick}
                            >
                              {t("Create Account")}
                            </Button.Nav>
                            <Box minW="element.lg" />
                          </>
                        )}
                        <Button.Nav
                          data-testid="navbar-login"
                          variant="outline"
                          colorPalette="secondary"
                          onClick={logInOnClick}
                        >
                          {t("Log In")}
                        </Button.Nav>
                      </>
                    )}
                  </Box>
                  <Button.Nav
                    data-testid="navbar-mobile-user-menu"
                    data-sentry-mask
                    rightIcon={<Icon.ChevronDown boxSize="element.md" />}
                    display={{ base: "flex", sm: "none" }}
                    aria-label="Main menu"
                    onClick={mobileMenuOnOpen}
                  >
                    {isLoggedIn && user?.profile_picture ? (
                      <ProfileImage
                        width={27}
                        height={27}
                        alt={t("User profile image")}
                        src={
                          user.profile_picture ||
                          formatRelativeStaticPath("images/no-avatar.jpg")
                        }
                      />
                    ) : (
                      <Box
                        boxSize="element.xl"
                        borderRadius="full"
                        backgroundColor="gray.400"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon.User.Solid
                          boxSize="element.md"
                          color="light.100"
                        />
                      </Box>
                    )}
                  </Button.Nav>
                </>
              ) : null}
            </Flex>
          </PageMaxWidth>
        </Flex>
      </Container>
      <NavBarBanners
        isDetailsPage={isDetailPage}
        isReactNativeApp={isReactNativeApp}
      />
    </>
  );
};

export default NavBar;
